/**
 * 借贷账户「结算」内联表单（收回 / 还款 / 核销）—— `DR-047` · 批 82
 *
 * ## 依据与纪律
 *
 * - 「动作可选项」由 `features/contra-settle.ts` 单点给出（本组件**不判**哪个 kind 能用什么）；
 * - 金额解析、超额提示、核销二次确认同理；
 * - 🔴 **不弹 Dialog**（与 `AccountForm` 同取向）：内联可静态渲染断言，也不用浏览器弹窗；
 * - 🔴 超额**只提示不拦截**（`AC-Q2.6`：允许、不截断、给提示）；
 * - 🔴 核销**要第二次点击**（`AC-Q2.5`：仅在"确定永不再报 / 不再还"时可用）——
 *   与服务端无关，服务端判不了意图。
 */

import { useState, type ReactElement } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ContraKind,
  SettlementType,
  type AccountView,
  type SettleContraResultView,
} from '@jiaying/shared';
import { settleContra } from '../api/endpoints.js';
import {
  buildSettleRequest,
  emptySettleDraft,
  needsCounterAccount,
  overSettleHint,
  settleActionsFor,
  settleLabel,
  WAIVE_CONFIRM_HINT,
  type SettleDraft,
} from '../features/contra-settle.js';
import { ApiError } from '../api/http.js';
import { AccountPicker } from './AccountPicker.js';

function apiMessages(e: unknown): readonly string[] {
  if (!(e instanceof ApiError)) return [String(e)];
  const details = e.body.details as { issues?: unknown } | undefined;
  const issues = Array.isArray(details?.issues)
    ? details.issues.filter((x): x is string => typeof x === 'string')
    : [];
  return issues.length > 0 ? issues : [e.message];
}

export interface SettleFormProps {
  account: AccountView;
  /** 账本本地日历日（默认日期） */
  today: string;
  /** 可选的收付款账户（🔴 由页面从**账户列表**过滤好传入 —— 本组件不猜哪些能选） */
  counterOptions: readonly { id: string; label: string }[];
  busy: boolean;
  onSettled: (result: SettleContraResultView) => void;
  onCancel: () => void;
}

export function SettleForm({
  account,
  today,
  counterOptions,
  busy,
  onSettled,
  onCancel,
}: SettleFormProps): ReactElement {
  const actions = settleActionsFor(account.contraKind);
  const [draft, setDraft] = useState<SettleDraft>(() => emptySettleDraft(account.contraKind, today));
  const [errors, setErrors] = useState<readonly string[]>([]);
  /** 核销的第二次点击（`AC-Q2.5`） */
  const [waiveArmed, setWaiveArmed] = useState(false);
  const [pending, setPending] = useState(false);

  const hint = overSettleHint(draft.amountText, account.balance, account.currency);

  const submit = (): void => {
    const built = buildSettleRequest(draft);
    if (!built.ok) {
      setErrors(built.errors);
      return;
    }
    // 🔴 核销要第二次点击：第一次只"上膛"，不发请求
    if (built.request.type === SettlementType.Waive && !waiveArmed) {
      setWaiveArmed(true);
      setErrors([]);
      return;
    }
    setErrors([]);
    setPending(true);
    settleContra(account.id, built.request)
      .then((r) => onSettled(r))
      .catch((e: unknown) => setErrors(apiMessages(e)))
      .finally(() => setPending(false));
  };

  const disabled = busy || pending;

  return (
    <Card variant="outlined" data-testid={`settle-form-${account.id}`}>
      <CardContent>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          结算 · {account.name}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          本金往来不计收支 —— 收回 / 还款只是钱的位置变了，不是花掉或赚到
        </Typography>

        {errors.length > 0 && (
          <Alert severity="error" sx={{ mt: 1.5 }} data-testid="settle-errors">
            <Stack component="ul" sx={{ m: 0, pl: 2 }}>
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </Stack>
          </Alert>
        )}

        {waiveArmed && (
          <Alert severity="warning" sx={{ mt: 1.5 }} data-testid="settle-waive-hint">
            {WAIVE_CONFIRM_HINT}
          </Alert>
        )}

        <Divider sx={{ my: 1.5 }} />

        <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { md: '1fr 1fr' } }}>
          <TextField
            select
            label="动作"
            value={draft.type}
            onChange={(e) => {
              setDraft((d) => ({ ...d, type: e.target.value as SettlementType, counterAccountId: '' }));
              setErrors([]);
              setWaiveArmed(false);
            }}
            inputProps={{ 'data-testid': 'settle-type' }}
            disabled={disabled}
            helperText="核销只在「确定不会再还 / 报」时用"
          >
            {actions.map((a) => (
              <MenuItem key={a} value={a}>
                {settleLabel(a)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="金额"
            value={draft.amountText}
            onChange={(e) => {
              setDraft((d) => ({ ...d, amountText: e.target.value }));
              setErrors([]);
              setWaiveArmed(false);
            }}
            inputProps={{ 'data-testid': 'settle-amount', inputMode: 'decimal' }}
            disabled={disabled}
            helperText={`${account.currency} · 可超过未结余额`}
          />
          <TextField
            label="日期"
            type="date"
            value={draft.dateText}
            onChange={(e) => {
              setDraft((d) => ({ ...d, dateText: e.target.value }));
              setErrors([]);
            }}
            // 🆕 批 `115`（T4）：换原生日期控件 ⇒ 有日历可选，仍能键入
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'data-testid': 'settle-date' }}
            disabled={disabled}
          />
          {needsCounterAccount(draft.type) && (
            /*
              🆕 批 `115`（T4 · `DR-066` §5）：**换控件，不换候选**。
              🔴 候选仍是页面传进来的 `counterOptions`（`canBeCounterAccount` 已筛过），
              顺序原样；这里只把"只能点"的 `TextField select` 换成可键入拼音的选择器。
              🔴 `testId` 保留 `settle-counter` —— 它是既有断言的靶子。
            */
            <AccountPicker
              label={draft.type === SettlementType.Collect ? '收款账户' : '付款账户'}
              accounts={counterOptions.map((o) => ({ id: o.id, name: o.label }))}
              value={draft.counterAccountId}
              onChange={(id) => {
                setDraft((d) => ({ ...d, counterAccountId: id }));
                setErrors([]);
              }}
              disabled={disabled}
              testId="settle-counter"
              helperText="同币种账户"
            />
          )}
        </Box>

        <TextField
          fullWidth
          label="备注"
          value={draft.note}
          onChange={(e) => {
            setDraft((d) => ({ ...d, note: e.target.value }));
            setErrors([]);
          }}
          inputProps={{ 'data-testid': 'settle-note', maxLength: 200 }}
          disabled={disabled}
          sx={{ mt: 1.5 }}
        />

        {hint !== null && (
          <Alert severity="info" sx={{ mt: 1.5 }} data-testid="settle-over-hint">
            {hint}
          </Alert>
        )}

        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            onClick={submit}
            disabled={disabled}
            data-testid="settle-submit"
            color={waiveArmed && draft.type === SettlementType.Waive ? 'error' : 'primary'}
          >
            {waiveArmed && draft.type === SettlementType.Waive ? '确认核销' : '保存'}
          </Button>
          <Button onClick={onCancel} disabled={disabled}>
            取消
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

/** 供页面过滤"可作收付款账户"的判据（🔴 与 `ContraKind` 无关：只看类型与币种） */
export function canBeCounterAccount(a: AccountView, currency: string): boolean {
  if (a.type === 'contra') return false; // 借贷账户之间直接对冲会让明细读不懂
  if (a.currency !== currency) return false;
  return a.type === 'debit' || a.type === 'cash' || a.type === 'ewallet' || a.type === 'credit';
}

/** `ContraKind` 是否属于"别人欠我"一侧（页面选"收款/付款"文案时用得上） */
export function isInflowKind(kind: ContraKind | null): boolean {
  return kind === ContraKind.Receivable || kind === ContraKind.Advance || kind === ContraKind.Reimburse;
}
